import type {
  AgentEndEvent,
  AgentEvent,
  AgentInterruptEvent,
  AgentMessage,
  AgentStartEvent,
  AgentStreamEndEvent,
  AgentStreamEvent,
  AgentStreamStartEvent,
  AgentToolEndEvent,
  AgentToolStartEvent,
  AgentToolProgressEvent,
  AgentToolMessageEvent,
} from "../types/ai_agent";

import { useSSEStream } from "./use_sse_stream";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function joinUrl(baseUrl: string | undefined, path: string) {
  if (!baseUrl) {
    return path;
  }

  const trimmed = baseUrl.replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;

  return `${trimmed}${p}`;
}

function agentStreamUrl(agentId: string) {
  const base = agentId.replace(/\/+$/, "");

  return `${base}/stream`;
}

export type UseAgentProps<
  TState extends Record<string, any> = Record<string, any>,
> = {
  /**
   * Agent ID. In this case we use it as the route path to the agent.
   */
  agentId: string;

  /**
   * Full base URL to the AI Agent service. By default, the AI_AGENT_URL
   * env will be used.
   */
  baseUrl?: string;

  /**
   * Conversation threadId defines the agent "memory". If changing the threadId,
   * the agent will forget the previous conversations.
   */
  threadId?: string;

  /**
   * Initial agent state sent in requests.
   */
  state: Partial<TState>;

  /**
   * Optional request headers (e.g. api-key)
   */
  headers?: Record<string, string>;

  /**
   * Logs all incoming SSE events to the console.
   */
  debug?: boolean;
};

export type Listener<T extends AgentEvent> = (event: T) => any | Promise<any>;

export type RunAgentOptions<
  TState extends Record<string, any> = Record<string, any>,
> = {
  messages?: AgentMessage[];
  threadId?: string;
  state?: Partial<TState>;
  headers?: Record<string, string>;
};

export type Agent = ReturnType<typeof useAgent>;

export function useAgent<
  TState extends Record<string, any> = Record<string, any>,
>(opts: UseAgentProps<TState>) {
  const [threadId, setThreadId] = useState(
    opts.threadId ?? crypto.randomUUID(),
  );
  const [state, setState] = useState<Partial<TState>>(opts.state ?? {});

  const listenersRef = useRef({
    streamStart: new Set<Listener<AgentStreamStartEvent>>(),
    agentStart: new Set<Listener<AgentStartEvent>>(),
    agentStream: new Set<Listener<AgentStreamEvent>>(),
    agentEnd: new Set<Listener<AgentEndEvent>>(),
    agentInterrupt: new Set<Listener<AgentInterruptEvent>>(),
    toolStart: new Set<Listener<AgentToolStartEvent>>(),
    toolEnd: new Set<Listener<AgentToolEndEvent>>(),
    toolProgress: new Set<Listener<AgentToolProgressEvent>>(),
    toolMessage: new Set<Listener<AgentToolMessageEvent>>(),
    streamEnd: new Set<Listener<AgentStreamEndEvent>>(),
  });

  const stateRef = useRef(state);
  const threadIdRef = useRef(threadId);
  const baseHeadersRef = useRef<Record<string, string>>(opts.headers ?? {});

  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    baseHeadersRef.current = opts.headers ?? {};
  }, [opts.headers]);

  const { status, error, messages: streamMessages, start, stop, clear } =
    useSSEStream<any>();

  const isRunning = status === "connecting" || status === "open";
  const statusRef = useRef(status);
  const errorRef = useRef(error);
  const isRunningRef = useRef(isRunning);
  const debugRef = useRef(!!opts.debug);
  const processedEventCountRef = useRef(0);

  statusRef.current = status;
  errorRef.current = error;
  isRunningRef.current = isRunning;
  debugRef.current = !!opts.debug;

  const runUrl = useMemo(
    () =>
      joinUrl(
        opts.baseUrl || import.meta.env.VITE_API_URL,
        agentStreamUrl(opts.agentId),
      ),
    [opts.baseUrl, opts.agentId],
  );

  const emit = useCallback(
    <T extends AgentEvent>(set: Set<Listener<T>>, event: T) => {
      for (const closure of set) {
        try {
          const result = closure(event);

          void Promise.resolve(result).catch((err) => {
            if (debugRef.current) {
              console.error("[useAgent] listener error", err);
            }
          });
        } catch (err) {
          if (debugRef.current) {
            console.error("[useAgent] listener error", err);
          }
        }
      }
    },
    [],
  );

  useEffect(() => {
    if (streamMessages.length === 0) {
      processedEventCountRef.current = 0;
      return;
    }

    for (
      let i = processedEventCountRef.current;
      i < streamMessages.length;
      i++
    ) {
      const streamMessage = streamMessages[i];
      const eventType: AgentEvent["event"] = streamMessage.event ??
        streamMessage.data?.event;
      const event: AgentEvent = {
        event: eventType,
        data: streamMessage.data?.data ?? streamMessage.data,
      };

      if (debugRef.current) {
        console.log("[useAgent] SSE event", {
          event: eventType,
          data: event.data,
        });
      }

      switch (eventType) {
        case "stream_start":
          emit(
            listenersRef.current.streamStart,
            event as AgentStreamStartEvent,
          );
          break;
        case "agent_start":
          emit(listenersRef.current.agentStart, event as AgentStartEvent);
          break;
        case "agent_stream":
          emit(listenersRef.current.agentStream, event as AgentStreamEvent);
          break;
        case "agent_end":
          emit(listenersRef.current.agentEnd, event as AgentEndEvent);
          break;
        case "agent_interrupt":
          emit(
            listenersRef.current.agentInterrupt,
            event as AgentInterruptEvent,
          );
          break;
        case "tool_start":
          emit(listenersRef.current.toolStart, event as AgentToolStartEvent);
          break;
        case "tool_end":
          emit(listenersRef.current.toolEnd, event as AgentToolEndEvent);
          break;
        case "tool_progress":
          emit(listenersRef.current.toolProgress, event as AgentToolProgressEvent);
          break;
        case "tool_message":
          emit(listenersRef.current.toolMessage, event as AgentToolMessageEvent);
          break;
        case "stream_end":
          emit(listenersRef.current.streamEnd, event as AgentStreamEndEvent);
          break;
      }
    }

    processedEventCountRef.current = streamMessages.length;
  }, [emit, streamMessages]);

  return useMemo(() => {
    return {
      /**
       * Get the current conversation threadId.
       */
      get threadId() {
        return threadIdRef.current;
      },

      /**
       * Get the current agent state.
       */
      get state() {
        return stateRef.current;
      },

      /**
       * Validate if the agent is currently running.
       */
      get isRunning() {
        return isRunningRef.current;
      },

      /**
       * Validate the current agent status.
       */
      get status() {
        return statusRef.current;
      },

      /**
       * Get the error thrown by the agent if exists.
       */
      get error() {
        return errorRef.current;
      },

      /**
       * Set a new conversation threadId.
       */
      setThreadId: (threadId: string) => {
        threadIdRef.current = threadId;

        setThreadId(threadId);
      },

      /**
       * Set a new agent state.
       *
       * @example
       * ```ts
       * agent.setState('user_name', 'John Doe')
       *
       * agent.setState({ user_name: 'John Doe', agent_model: 'openai:gpt-4.1' })
       * ```
       */
      setState: <K extends keyof TState>(
        key: K | Partial<TState>,
        value?: TState[K],
      ) => {
        if (typeof key === "object" && key !== null) {
          stateRef.current = key as Partial<TState>;

          setState(key as Partial<TState>);

          return;
        }

        stateRef.current = {
          ...(stateRef.current as any),
          [key as K]: value as TState[K],
        };

        setState((prev) => ({
          ...(prev as any),
          [key as K]: value as TState[K],
        }));
      },

      /**
       * Clear all events.
       */
      clearEvents: () => clear(),

      /**
       * Abort the current agent run.
       */
      abortRun: () => stop(),

      /**
       * Run the agent
       *
       * @example
       * ```ts
       * agent.runAgent({ messages: [{ role: 'user', content: 'Hello, how are you?' }] })
       * agent.runAgent({ threadId: '123', state: { user_name: 'John Doe' } })
       * agent.runAgent({ headers: { 'api-key': '123' } })
       * ```
       */
      runAgent: async (options: RunAgentOptions<TState> = {}) => {
        clear();

        processedEventCountRef.current = 0;

        const mergedHeaders = {
          ...(baseHeadersRef.current ?? {}),
          ...(options.headers ?? {}),
        };

        const payload = {
          run_id: crypto.randomUUID(),
          tools: [],
          thread_id: options.threadId ?? threadIdRef.current,
          state: options.state ?? stateRef.current,
          messages: options.messages,
        };

        if (debugRef.current) {
          console.log("[useAgent] runAgent request", {
            url: runUrl,
            body: payload,
            headers: mergedHeaders,
          });
        }

        start({
          url: runUrl,
          headers: mergedHeaders,
          body: payload,
        });
      },

      /**
       * Listen to the stream_start event.
       *
       * @example
       * ```ts
       * agent.onStreamStart((event) => {
       *   console.log('Stream started', event);
       * });
       * ```
       */
      onStreamStart: (cb: Listener<AgentStreamStartEvent>) => {
        listenersRef.current.streamStart.add(cb);

        return () => {
          listenersRef.current.streamStart.delete(cb);
        };
      },

      /**
       * Listen to the stream_end event.
       *
       * @example
       * ```ts
       * agent.onStreamEnd((event) => {
       *   console.log('Stream ended', event);
       * });
       * ```
       */
      onStreamEnd: (cb: Listener<AgentStreamEndEvent>) => {
        listenersRef.current.streamEnd.add(cb);

        return () => {
          listenersRef.current.streamEnd.delete(cb);
        };
      },

      /**
       * Listen to the agent_start event.
       *
       * @example
       * ```ts
       * agent.onAgentStart((event) => {
       *   console.log('Agent started', event);
       * });
       * ```
       */
      onAgentStart: (cb: Listener<AgentStartEvent>) => {
        listenersRef.current.agentStart.add(cb);

        return () => {
          listenersRef.current.agentStart.delete(cb);
        };
      },

      /**
       * Listen to the agent_stream event.
       *
       * @example
       * ```ts
       * agent.onAgentStream((event) => {
       *   console.log('Agent streamed', event);
       * });
       * ```
       */
      onAgentStream: (cb: Listener<AgentStreamEvent>) => {
        listenersRef.current.agentStream.add(cb);

        return () => {
          listenersRef.current.agentStream.delete(cb);
        };
      },

      /**
       * Listen to the agent_end event.
       *
       * @example
       * ```ts
       * agent.onAgentEnd((event) => {
       *   console.log('Agent ended', event);
       * });
       * ```
       */
      onAgentEnd: (cb: Listener<AgentEndEvent>) => {
        listenersRef.current.agentEnd.add(cb);

        return () => {
          listenersRef.current.agentEnd.delete(cb);
        };
      },

      /**
       * Listen to the agent_interrupt event.
       *
       * @example
       * ```ts
       * agent.onAgentInterrupt((event) => {
       *   console.log('Agent interrupted', event);
       * });
       * ```
       */
      onAgentInterrupt: (cb: Listener<AgentInterruptEvent>) => {
        listenersRef.current.agentInterrupt.add(cb);

        return () => {
          listenersRef.current.agentInterrupt.delete(cb);
        };
      },

      /**
       * Listen to the tool_start event.
       *
       * @example
       * ```ts
       * agent.onToolStart((event) => {
       *   console.log('Tool started', event);
       * });
       * ```
       */
      onToolStart: (cb: Listener<AgentToolStartEvent>) => {
        listenersRef.current.toolStart.add(cb);

        return () => {
          listenersRef.current.toolStart.delete(cb);
        };
      },

      /**
       * Listen to the tool_end event.
       *
       * @example
       * ```ts
       * agent.onToolEnd((event) => {
       *   console.log('Tool ended', event);
       * });
       */
      onToolEnd: (cb: Listener<AgentToolEndEvent>) => {
        listenersRef.current.toolEnd.add(cb);

        return () => {
          listenersRef.current.toolEnd.delete(cb);
        };
      },

      onToolProgress: (cb: Listener<AgentToolProgressEvent>) => {
        listenersRef.current.toolProgress.add(cb);

        return () => {
          listenersRef.current.toolProgress.delete(cb);
        };
      },

      onToolMessage: (cb: Listener<AgentToolMessageEvent>) => {
        listenersRef.current.toolMessage.add(cb);

        return () => {
          listenersRef.current.toolMessage.delete(cb);
        };
      },
    };
  }, [clear, runUrl, start, stop]);
}
